import type { Editor, Range } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

import { TiptapNodeType } from '../../tiptapTypes';
import { getChildTaskList } from './taskListItemNodeUtils';

export type TaskDeletionInfo = {
    isLastTask: boolean;
};

/**
 * Gets the document range required to remove a task item and its nested sub-tasks.
 *
 * Accepts a raw document so callers can re-derive the range against an in-progress
 * transaction (`transaction.doc`) before dispatching, not just the editor's current state.
 */
export const getTaskItemDeletionRange = (doc: ProseMirrorNode, position: number): Range | null => {
    const taskItem = doc.nodeAt(position);

    if (!taskItem) return null;

    const $position = doc.resolve(position);
    const isOnlyChildOfList = $position.parent.childCount === 1;
    const isLastTask = $position.depth === 1 && isOnlyChildOfList;

    if (isLastTask) return null;

    // Deleting the only child of a nested list would leave an empty taskList, which the
    // schema forbids — remove the wrapping list instead.
    const from = isOnlyChildOfList ? $position.before() : position;
    const to = isOnlyChildOfList ? $position.after() : position + taskItem.nodeSize;

    return { from, to };
};

/**
 * Deletes the task item at the given position, along with any nested sub-tasks.
 *
 * If the task is the last remaining top-level task, the document is left unchanged and the
 * callback is invoked with `isLastTask: true` so the consumer can decide how to handle it.
 */
export const deleteTaskItem = (
    editor: Editor,
    position: number,
    onDeleteTask?: (info: TaskDeletionInfo) => void,
): void => {
    if (!editor.state.doc.nodeAt(position)) return;

    const deletionRange = getTaskItemDeletionRange(editor.state.doc, position);

    if (!deletionRange) {
        onDeleteTask?.({ isLastTask: true });
        return;
    }

    editor.commands.deleteRange(deletionRange);
    onDeleteTask?.({ isLastTask: false });
};

/**
 * Collects the ranges to remove every completed task in a task list, and reports whether that
 * would empty the list.
 *
 * A completed task takes its whole subtree with it, so its sub-tasks are never visited. Emptying a
 * nested list is not allowed by the schema, so the wrapping list is removed in place of its tasks.
 */
const collectCompletedTaskRanges = (taskList: ProseMirrorNode, taskListPosition: number, ranges: Range[]): boolean => {
    let taskItemPosition = taskListPosition + 1;
    let isEmptiedByDeletions = true;

    taskList.forEach((taskItem) => {
        const position = taskItemPosition;
        taskItemPosition += taskItem.nodeSize;

        if (taskItem.attrs.checked) {
            ranges.push({ from: position, to: position + taskItem.nodeSize });
            return;
        }

        isEmptiedByDeletions = false;

        // Sub-tasks of an outstanding task can still be completed
        const nestedTaskList = getChildTaskList(taskItem);
        if (!nestedTaskList) return;

        // Offsets are relative to the task item's content, which starts one token in
        const nestedTaskListPosition = position + 1 + nestedTaskList.offset;
        const rangeCountBeforeNesting = ranges.length;

        if (!collectCompletedTaskRanges(nestedTaskList.node, nestedTaskListPosition, ranges)) return;

        ranges.length = rangeCountBeforeNesting;
        ranges.push({ from: nestedTaskListPosition, to: nestedTaskListPosition + nestedTaskList.node.nodeSize });
    });

    return isEmptiedByDeletions;
};

/**
 * Deletes every completed task in the editor's list, along with each one's sub-tasks, in a single
 * transaction so the whole removal is one undo step.
 *
 * A list where every task is completed keeps one empty outstanding task, since the schema requires
 * the list to be non-empty.
 */
export const deleteCompletedTaskItems = (editor: Editor): void => {
    const { doc } = editor.state;
    const taskList = getChildTaskList(doc);

    if (!taskList) return;

    // Document offsets are absolute positions
    const taskListPosition = taskList.offset;
    const ranges: Range[] = [];
    const isListEmptiedByDeletions = collectCompletedTaskRanges(taskList.node, taskListPosition, ranges);

    if (!ranges.length) return;

    const { tr } = editor.state;

    if (isListEmptiedByDeletions) {
        const emptyTaskItem = editor.schema.nodes[TiptapNodeType.taskItem].createAndFill();

        if (!emptyTaskItem) return;

        tr.replaceWith(taskListPosition + 1, taskListPosition + taskList.node.nodeSize - 1, emptyTaskItem);
    } else {
        // Applied last range first, so each range stays valid against the document it was resolved in
        ranges.reverse().forEach(({ from, to }) => tr.delete(from, to));
    }

    editor.view.dispatch(tr);
};
